import React, { useRef, useEffect } from 'react';

const DATA_COLORS = [
  '#1a6cff', '#0a9e63', '#6d3fc0', '#0088b5', '#c47800', '#d93025', '#c02060',
];

function hexRGB(hex) {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function fibSphere(n) {
  const pts = [];
  const phi = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < n; i++) {
    const y = 1 - (i / (n - 1)) * 2;
    const rad = Math.sqrt(1 - y * y);
    const th = phi * i;
    pts.push({ x: Math.cos(th) * rad, y, z: Math.sin(th) * rad });
  }
  return pts;
}

function dist3d(a, b) {
  return Math.sqrt((a.tx - b.tx) ** 2 + (a.ty - b.ty) ** 2 + (a.tz - b.tz) ** 2);
}

function DataGlobe() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;

    const ctx = cv.getContext('2d');
    let W;
    let CX;
    let CY;
    let R;
    let animFrameId;

    const dpr = () => window.devicePixelRatio || 1;

    function setSize() {
      const d = dpr();
      const s = cv.offsetWidth;
      cv.width = Math.round(s * d);
      cv.height = Math.round(s * d);
      W = cv.width;
      CX = CY = W / 2;
      R = W * 0.41;
    }
    setSize();

    const resizeObserver = new ResizeObserver(() => setSize());
    resizeObserver.observe(cv);

    let rotY = 0;
    let rotX = 0.35;
    let rotYV = 0.0014;
    let rotXV = 0.0001;
    let dragging = false;
    let lastMX = 0;
    let lastMY = 0;

    function project(x, y, z) {
      const cosY = Math.cos(rotY);
      const sinY = Math.sin(rotY);
      const x1 = x * cosY - z * sinY;
      const z1 = x * sinY + z * cosY;
      const cosX = Math.cos(rotX);
      const sinX = Math.sin(rotX);
      const y2 = y * cosX - z1 * sinX;
      const z2 = y * sinX + z1 * cosX;
      const depth = z2;
      const brightness = Math.max(0, (depth + 0.14) / 1.14);
      const sc = 1 + depth * 0.05;
      return {
        sx: CX + x1 * R * sc,
        sy: CY - y2 * R * sc,
        depth,
        brightness,
        visible: depth > -0.06,
      };
    }

    const spherePts = fibSphere(200);
    const nodes = spherePts.map((sp, i) => ({
      tx: sp.x,
      ty: sp.y,
      tz: sp.z,
      cx: sp.x,
      cy: sp.y,
      cz: sp.z,
      arrived: true,
      arrivalTime: null,
      col: DATA_COLORS[i % DATA_COLORS.length],
      neighbors: [],
      pulse: Math.random() * Math.PI * 2,
      baseSize: 18 + Math.random() * 10,
      idx: i,
    }));

    nodes.forEach((n, i) => {
      const ds = nodes.map((m, j) => ({ j, d: dist3d(n, m) })).sort((a, b) => a.d - b.d);
      n.neighbors = ds.slice(1, 4).map((x) => x.j);
    });

    const onMouseDown = (e) => {
      dragging = true;
      lastMX = e.clientX;
      lastMY = e.clientY;
      cv.style.cursor = 'grabbing';
    };
    const onWindowMouseMove = (e) => {
      if (!dragging) return;
      rotYV = (e.clientX - lastMX) * 0.006;
      rotXV = (e.clientY - lastMY) * 0.001;
      lastMX = e.clientX;
      lastMY = e.clientY;
    };
    const onWindowMouseUp = () => {
      if (!dragging) return;
      dragging = false;
      cv.style.cursor = 'grab';
    };
    const onTouchStart = (e) => {
      dragging = true;
      lastMX = e.touches[0].clientX;
      lastMY = e.touches[0].clientY;
    };
    const onTouchMove = (e) => {
      if (!dragging) return;
      rotYV = (e.touches[0].clientX - lastMX) * 0.006;
      rotXV = (e.touches[0].clientY - lastMY) * 0.001;
      lastMX = e.touches[0].clientX;
      lastMY = e.touches[0].clientY;
    };
    const onTouchEnd = () => {
      dragging = false;
    };

    cv.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onWindowMouseMove);
    window.addEventListener('mouseup', onWindowMouseUp);
    cv.addEventListener('touchstart', onTouchStart, { passive: true });
    cv.addEventListener('touchmove', onTouchMove, { passive: true });
    cv.addEventListener('touchend', onTouchEnd);

    function drawGlobeBase() {
      const grd = ctx.createRadialGradient(CX, CY, 0, CX, CY, R);
      grd.addColorStop(0, '#ffffff');
      grd.addColorStop(0.5, '#f9f9fd');
      grd.addColorStop(1, '#eeedf6');
      ctx.beginPath();
      ctx.arc(CX, CY, R, 0, Math.PI * 2);
      ctx.fillStyle = grd;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(CX, CY, R, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(100,100,180,0.10)';
      ctx.lineWidth = 1.5 * dpr();
      ctx.stroke();
    }

    function drawGrid() {
      const d = dpr();
      ctx.save();
      ctx.beginPath();
      ctx.arc(CX, CY, R, 0, Math.PI * 2);
      ctx.clip();

      ctx.strokeStyle = 'rgba(80,80,180,0.06)';
      ctx.lineWidth = 0.7 * d;
      for (let lat = -75; lat <= 75; lat += 15) {
        const yv = Math.sin((lat * Math.PI) / 180);
        const rv = Math.cos((lat * Math.PI) / 180);
        ctx.beginPath();
        let first = true;
        for (let i = 0; i <= 72; i++) {
          const th = (i / 72) * Math.PI * 2;
          const p = project(rv * Math.cos(th), yv, rv * Math.sin(th));
          if (first) {
            ctx.moveTo(p.sx, p.sy);
            first = false;
          } else {
            ctx.lineTo(p.sx, p.sy);
          }
        }
        ctx.stroke();
      }

      ctx.strokeStyle = 'rgba(80,80,180,0.04)';
      ctx.lineWidth = 0.5 * d;
      for (let lon = 0; lon < 180; lon += 20) {
        const th = (lon * Math.PI) / 180;
        ctx.beginPath();
        let first = true;
        for (let i = 0; i <= 72; i++) {
          const la = (i / 72) * Math.PI * 2 - Math.PI;
          const yv = Math.sin(la);
          const rv = Math.cos(la);
          const p = project(rv * Math.cos(th), yv, rv * Math.sin(th));
          if (first) {
            ctx.moveTo(p.sx, p.sy);
            first = false;
          } else {
            ctx.lineTo(p.sx, p.sy);
          }
        }
        ctx.stroke();
      }

      ctx.restore();
    }

    function drawNodeDot(p, n, sz, opacity) {
      const [r, g, b] = hexRGB(n.col);
      ctx.beginPath();
      ctx.arc(p.sx, p.sy, sz * 0.78, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${r},${g},${b},${opacity})`;
      ctx.fill();
    }

    function drawBackNodes(arrivedBack) {
      const d = dpr();
      arrivedBack.forEach(({ n, p }) => {
        const backness = Math.max(0, Math.min(1, (-p.depth - 0.06) / 0.94));
        const opacity = 0.28 - backness * 0.18;
        const scale = 1.0 - backness * 0.38;
        const sz = n.baseSize * scale * 0.9 * d;
        if (sz < 4) return;
        drawNodeDot(p, n, sz, opacity);
      });
    }

    function drawConnections(arrivedFront) {
      const d = dpr();
      arrivedFront.forEach(({ n, p }) => {
        n.neighbors.forEach((ni) => {
          if (ni <= n.idx) return;
          const nb = nodes[ni];
          if (!nb.arrived) return;
          const np = project(nb.cx, nb.cy, nb.cz);
          if (!np.visible) return;
          const minB = Math.min(p.brightness, np.brightness);
          if (minB < 0.05) return;
          const [r, g, b] = hexRGB(n.col);
          ctx.beginPath();
          ctx.moveTo(p.sx, p.sy);
          ctx.lineTo(np.sx, np.sy);
          ctx.strokeStyle = `rgba(${r},${g},${b},${minB * 0.12})`;
          ctx.lineWidth = 0.5 * d;
          ctx.stroke();
        });
      });
    }

    function drawFrontNodes(arrivedFront, ts) {
      const d = dpr();
      arrivedFront.forEach(({ n, p }) => {
        const b = p.brightness;
        if (b < 0.01) return;

        const pulse = 0.86 + Math.sin(ts * 0.0012 + n.pulse) * 0.14;
        const rawSz = n.baseSize * b * pulse * d;
        const sz = Math.max(12 * d, rawSz);
        const [r, g, bv] = hexRGB(n.col);

        const glowR = sz * 1.5;
        const grd = ctx.createRadialGradient(p.sx, p.sy, 0, p.sx, p.sy, glowR);
        grd.addColorStop(0, `rgba(${r},${g},${bv},${b * 0.10})`);
        grd.addColorStop(1, `rgba(${r},${g},${bv},0)`);
        ctx.beginPath();
        ctx.arc(p.sx, p.sy, glowR, 0, Math.PI * 2);
        ctx.fillStyle = grd;
        ctx.fill();

        if (n.arrivalTime) {
          const since = (ts - n.arrivalTime) / 1000;
          if (since < 1) {
            const rr = sz * (1 + since * 7);
            const ra = (1 - since) * b * 0.5;
            ctx.beginPath();
            ctx.arc(p.sx, p.sy, rr, 0, Math.PI * 2);
            ctx.strokeStyle = n.col;
            ctx.lineWidth = 1.5 * d;
            ctx.globalAlpha = ra;
            ctx.stroke();
            ctx.globalAlpha = 1;
          }
        }

        ctx.beginPath();
        ctx.arc(p.sx, p.sy, sz * 0.78, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${b * 0.88})`;
        ctx.fill();

        drawNodeDot(p, n, sz, b * 0.92);
      });
    }

    function drawSpecular() {
      ctx.save();
      ctx.beginPath();
      ctx.arc(CX, CY, R, 0, Math.PI * 2);
      ctx.clip();
      const hx = CX - R * 0.25;
      const hy = CY - R * 0.28;
      const grd = ctx.createRadialGradient(hx, hy, 0, hx, hy, R * 0.5);
      grd.addColorStop(0, 'rgba(255,255,255,0.30)');
      grd.addColorStop(0.4, 'rgba(255,255,255,0.08)');
      grd.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.beginPath();
      ctx.arc(CX, CY, R, 0, Math.PI * 2);
      ctx.fillStyle = grd;
      ctx.fill();
      ctx.restore();
    }

    function frame(ts) {
      rotY += rotYV;
      rotX += rotXV;
      if (!dragging) {
        rotYV += (0.0014 - rotYV) * 0.018;
        rotXV *= 0.96;
      }

      ctx.clearRect(0, 0, W, W);

      const arrivedFront = [];
      const arrivedBack = [];

      nodes.forEach((n) => {
        const p = project(n.cx, n.cy, n.cz);
        if (p.visible) arrivedFront.push({ n, p });
        else arrivedBack.push({ n, p });
      });

      arrivedFront.sort((a, b) => a.p.depth - b.p.depth);

      drawGlobeBase();
      drawGrid();
      drawBackNodes(arrivedBack);
      drawConnections(arrivedFront);
      drawFrontNodes(arrivedFront, ts);
      drawSpecular();

      animFrameId = requestAnimationFrame(frame);
    }
    animFrameId = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(animFrameId);
      resizeObserver.disconnect();
      cv.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onWindowMouseMove);
      window.removeEventListener('mouseup', onWindowMouseUp);
      cv.removeEventListener('touchstart', onTouchStart);
      cv.removeEventListener('touchmove', onTouchMove);
      cv.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  return (
    <div style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center' }}>
      <div
        style={{
          position: 'relative',
          width: '720px',
          height: '720px',
          maxWidth: 'min(96vw, 78vh)',
          maxHeight: 'min(96vw, 78vh)',
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            display: 'block',
            cursor: 'grab',
            touchAction: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            width: '100%',
            height: '100%',
          }}
        />
      </div>
    </div>
  );
}

export default DataGlobe;
